import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Toast } from 'primereact/toast';
import { getAuthUser } from '../../services/authApi';
import { fetchCarrierUsers } from '../../services/carrierApi';
import {
  AgentMailApiError,
  InboxConversation,
  InboxStatus,
  deleteInbox,
  deleteRequestType,
  enableInbox,
  formatFileSize,
  formatInboxDate,
  getInboxHistory,
  getInboxStatus,
} from '../../services/agentMailApi';

/* Underwriting AI Inbox — Carrier Admin only (route-guarded).
   Off until the admin enables it; once enabled it shows the carrier's AgentMail
   address and the request types the inbox understands. The Enable screen asks the
   server (GET /agentmail/inbox) whether the carrier is already enabled: Enable saves
   it (POST /agentmail/inbox), and Continue, available once it is saved, opens the
   inbox view, which loads its history and users from the AI API (/agentmail/*). */

const NOT_ENABLED: InboxStatus = { enabled: false, address: null, enabledAt: null, customTypes: [] };

interface RequestType {
  id: string;
  title: string;
  tag: string;
  description: string;
  icon: 'upload' | 'doc' | 'report' | 'tag';
  custom?: boolean;
  /** Server id of a custom type, used to delete it. */
  customId?: number;
}

const DEFAULT_TYPES: RequestType[] = [
  {
    id: 'loss-run',
    title: 'Loss Run Upload',
    tag: 'Loss Run Upload:',
    icon: 'upload',
    description:
      'Email loss run documents with the subject tag. Underwriting AI reads them, finds the insured or policy, and files what the email asks for.',
  },
  {
    id: 'mvr',
    title: 'MVR Upload',
    tag: 'MVR Upload:',
    icon: 'doc',
    description:
      'Email motor vehicle records with the subject tag. Underwriting AI matches each driver to the submission and attaches the MVR.',
  },
  {
    id: 'cab',
    title: 'CAB Report',
    tag: 'CAB Report:',
    icon: 'report',
    description:
      'Email a CAB report with the subject tag. Underwriting AI matches it to the insured and records what the report shows.',
  },
];

/* The three default tags share one card — the subject tag picks the document type. */
const DEFAULT_CARD: RequestType = {
  id: 'default',
  title: DEFAULT_TYPES.map(t => t.title).join(', ').replace(/, ([^,]*)$/, ' and $1'),
  tag: DEFAULT_TYPES[0].tag,
  icon: 'upload',
  description:
    'Email the documents with the matching subject tag. Underwriting AI reads the attachments, matches them to the insured, policy or driver, and files what the email asks for: loss runs on the submission, MVRs against each driver, and CAB report findings on the insured.',
};

/* The Mail format example: the subject label the AI API reads the quote id after, and example file kinds. */
const QUOTE_ID_SUBJECT = 'QuoteId:';
const MAIL_FORMAT_FILES = ['Loss run files', 'MVR files', 'CAB report files'];

const TYPE_ICONS: Record<RequestType['icon'], React.ReactNode> = {
  upload: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </>
  ),
  doc: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </>
  ),
  report: (
    <>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </>
  ),
  tag: (
    <>
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </>
  ),
};

const Svg: React.FC<{ size?: number; children: React.ReactNode }> = ({ size = 16, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const UnderwritingInbox: React.FC = () => {
  const toast = useRef<Toast>(null);
  const user = getAuthUser();
  const agencyName = user?.carrierName || 'Your Carrier';

  const [status, setStatus] = useState<InboxStatus>(NOT_ENABLED);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const [proceeded, setProceeded] = useState(false);
  const [userCount, setUserCount] = useState<number | null>(null);
  const [showFormat, setShowFormat] = useState(false);
  const [history, setHistory] = useState<InboxConversation[]>([]);
  const [historyKey, setHistoryKey] = useState(0);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historySearch, setHistorySearch] = useState('');
  const [selectedConv, setSelectedConv] = useState<string | null>(null);

  // The Mail format card is a dialog: Esc closes it, like its close button and a click outside it.
  useEffect(() => {
    if (!showFormat) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowFormat(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showFormat]);

  const enabled = status.enabled;
  /** The inbox view opens only once the server says it is enabled and the admin has pressed Continue. */
  const showInbox = enabled && proceeded;
  const inboxAddress = status.address ?? '';
  const customTypes: RequestType[] = status.customTypes.map(t => ({
    id: `custom-${t.id}`,
    customId: t.id,
    title: t.title,
    tag: t.tag,
    icon: 'tag',
    custom: true,
    description: `Email with the subject tag “${t.tag}” and it is logged in your inbox history. Automated handling for custom request types is coming soon.`,
  }));

  const filteredHistory = history.filter(c =>
    `${c.subject} ${c.sender}`.toLowerCase().includes(historySearch.toLowerCase())
  );
  const activeConv = history.find(c => c.id === selectedConv) || null;

  const errorText = (err: unknown) =>
    err instanceof AgentMailApiError ? err.message : 'Something went wrong. Please try again.';

  useEffect(() => {
    let cancelled = false;
    getInboxStatus()
      .then(s => !cancelled && setStatus(s))
      .catch(err => !cancelled && notify('warn', errorText(err)))
      .finally(() => !cancelled && setChecking(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showInbox) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    setHistoryError(null);
    getInboxHistory()
      .then(rows => !cancelled && setHistory(rows))
      .catch(err => !cancelled && setHistoryError(errorText(err)));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInbox, historyKey]);

  useEffect(() => {
    if (!showInbox) return;
    let cancelled = false;
    fetchCarrierUsers()
      .then(res => !cancelled && setUserCount(res.data.length))
      .catch(() => !cancelled && setUserCount(null));
    return () => { cancelled = true; };
  }, [showInbox]);

  const notify = (severity: 'success' | 'info' | 'warn', detail: string) =>
    toast.current?.show({ severity, summary: severity === 'success' ? 'Done' : 'Notice', detail, life: 2600 });

  const copy = (text: string, what: string) => {
    navigator.clipboard?.writeText(text)
      .then(() => notify('success', `${what} copied.`))
      .catch(() => notify('warn', 'Could not copy to clipboard.'));
  };

  const compose = (subject?: string) => {
    window.location.href = `mailto:${inboxAddress}${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`;
  };

  const handleEnable = async () => {
    setBusy(true);
    try {
      setStatus(await enableInbox());
      notify('success', 'Underwriting AI Inbox enabled.');
    } catch (err) {
      notify('warn', errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteInbox = async () => {
    if (!window.confirm('Delete the Underwriting AI Inbox for your carrier?')) return;
    setBusy(true);
    try {
      setStatus(await deleteInbox());
      setProceeded(false);
      setSelectedConv(null);
      notify('info', 'Underwriting AI Inbox deleted.');
    } catch (err) {
      notify('warn', errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const removeType = async (type: RequestType) => {
    if (type.customId === undefined) return;
    try {
      await deleteRequestType(type.customId);
      setStatus(prev => ({ ...prev, customTypes: prev.customTypes.filter(t => t.id !== type.customId) }));
    } catch (err) {
      notify('warn', errorText(err));
    }
  };

  const Header = (
    <div className="uw-inbox__header">
      <div className="uw-inbox__icon">
        <Svg size={20}>
          <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </Svg>
      </div>
      <div>
        <h1 className="uw-inbox__title">Underwriting AI Inbox</h1>
        <p className="uw-inbox__sub">
          One shared inbox for your underwriting team. Tag the subject, and Underwriting AI does the work.
        </p>
      </div>
    </div>
  );

  const TagList = (
    <>
      {DEFAULT_TYPES.map((t, i) => (
        <React.Fragment key={t.id}>
          <code>{t.tag}</code>
          {i < DEFAULT_TYPES.length - 2 ? ', ' : i === DEFAULT_TYPES.length - 2 ? ' or ' : ''}
        </React.Fragment>
      ))}
    </>
  );

  if (!showInbox) {
    return (
      <div className="dashboard">
        <Toast ref={toast} />
        <div className="uw-inbox">
          {Header}
          <div className="uw-inbox__card">
            <h2 className="uw-inbox__card-title">Enable Underwriting AI Inbox</h2>
            <p className="uw-inbox__text">
              Underwriting AI Inbox is part of the AI Agent Email add-on. Once enabled, your
              carrier gets one shared email address: anyone on your underwriting team emails it
              from their work address, starts the subject with a tag such as {TagList}, and
              Underwriting AI handles it with their portal access.
            </p>
            <div className="uw-inbox__actions">
              <button className="uw-inbox__btn" onClick={handleEnable} disabled={busy || checking || enabled}>
                <Svg size={15}>
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                </Svg>
                {busy ? 'Enabling…' : enabled ? 'Enabled' : 'Enable'}
              </button>
              <button
                className="uw-inbox__btn uw-inbox__btn--outline"
                onClick={() => setProceeded(true)}
                disabled={busy || checking || !enabled}
                title={enabled ? undefined : 'Enable the inbox first'}
              >
                Continue
                <Svg size={15}>
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </Svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const allTypes = [DEFAULT_CARD, ...customTypes];

  return (
    <div className="dashboard">
      <Toast ref={toast} />
      <div className="uw-inbox">
        {Header}

        <div className="uw-steps">
          <div className="uw-step">
            <span className="uw-step__num">1</span>
            <div>
              <div className="uw-step__title">Copy the carrier address</div>
              <p>Everyone at your carrier uses the same inbox, from their own work address.</p>
            </div>
          </div>
          <div className="uw-step">
            <span className="uw-step__num">2</span>
            <div>
              <div className="uw-step__title">Start the subject with a request tag</div>
              <p>{TagList}, or one of your own. The tag picks the execution prompt.</p>
            </div>
          </div>
          <div className="uw-step">
            <span className="uw-step__num">3</span>
            <div>
              <div className="uw-step__title">Get a reply on the same thread</div>
              <p>Underwriting AI processes it and replies. Track every request in the history below.</p>
            </div>
          </div>
        </div>

        <div className="uw-panel">
          <div className="uw-panel__top">
            <div>
              <div className="uw-eyebrow">Shared carrier inbox</div>
              <h2 className="uw-panel__name">{agencyName}</h2>
            </div>
            {userCount !== null && (
              <span className="uw-pill">
                <Svg size={14}>
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </Svg>
                {userCount} connected {userCount === 1 ? 'user' : 'users'}
              </span>
            )}
          </div>

          <div className="uw-address">
            <span className="uw-address__text">{inboxAddress}</span>
            <button className="uw-iconbtn" title="Copy address" aria-label="Copy address" onClick={() => copy(inboxAddress, 'Address')}>
              <Svg size={17}>
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </Svg>
            </button>
            <button className="uw-iconbtn" title="Compose email" aria-label="Compose email" onClick={() => compose()}>
              <Svg size={17}>
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </Svg>
            </button>
          </div>

          <p className="uw-panel__text">
            Email it from your work address and start the subject with a request tag such as {TagList}.
            Underwriting AI Inbox runs that type’s execution prompt with your portal access and replies on
            the same thread.{status.enabledAt ?` Enabled ${formatInboxDate(status.enabledAt)}.` : ''}
          </p>

          <div className="uw-panel__foot">
            <span className="uw-note">
              <Svg size={15}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </Svg>
              Only carrier admins can delete this inbox.
            </span>
            <button className="uw-danger" onClick={handleDeleteInbox} disabled={busy}>
              <Svg size={15}>
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </Svg>
              Delete inbox
            </button>
          </div>
        </div>

        <div className="uw-section">
          <h2 className="uw-section__title">
            <Svg size={17}>{TYPE_ICONS.tag}</Svg>
            Request types
          </h2>
          <button className="uw-link" onClick={() => setShowFormat(true)} aria-haspopup="dialog">
            <Svg size={15}>
              <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </Svg>
            Mail format
          </button>
        </div>

        {showFormat && createPortal(
          /* A fixed example of how to write the email: plain text, nothing here is an input, so it can't be edited. */
          <div
            className="modal-overlay"
            role="presentation"
            onMouseDown={e => { if (e.target === e.currentTarget) setShowFormat(false); }}
          >
            <div className="modal-card uw-format-modal" role="dialog" aria-modal="true" aria-label="Mail format">
              <div className="modal-card__header">
                <h3>Mail format</h3>
                <button className="icon-btn" onClick={() => setShowFormat(false)} aria-label="Close" autoFocus>
                  <Svg size={16}>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </Svg>
                </button>
              </div>

              <div className="uw-format">
                <div className="uw-format__row">
                  <span className="uw-format__label">To</span>
                  <span className="uw-format__value">{inboxAddress}</span>
                  <button className="uw-iconbtn" title="Copy address" aria-label="Copy address" onClick={() => copy(inboxAddress, 'Address')}>
                    <Svg size={16}>
                      <rect x="9" y="9" width="13" height="13" rx="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </Svg>
                  </button>
                </div>

                <div className="uw-format__row">
                  <span className="uw-format__label">Subject</span>
                  <span className="uw-format__value">
                    {QUOTE_ID_SUBJECT} <span className="uw-format__placeholder">quoteid value</span>
                  </span>
                  <button className="uw-iconbtn" title="Copy subject" aria-label="Copy subject" onClick={() => copy(`${QUOTE_ID_SUBJECT} `, 'Subject')}>
                    <Svg size={16}>
                      <rect x="9" y="9" width="13" height="13" rx="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </Svg>
                  </button>
                  <button className="uw-iconbtn" title="Compose email" aria-label="Compose email with this subject" onClick={() => compose(`${QUOTE_ID_SUBJECT} `)}>
                    <Svg size={16}>
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </Svg>
                  </button>
                </div>

                <div className="uw-format__row uw-format__row--body">
                  <span className="uw-format__label">Body</span>
                  <div className="uw-format__body">
                    <p>Hello,</p>
                    <p>Please file the attached documents on this quote:</p>
                    <ul>
                      {MAIL_FORMAT_FILES.map(f => <li key={f}>{f}</li>)}
                    </ul>
                    <p>Thank you</p>
                  </div>
                </div>

                <div className="uw-format__row uw-format__row--body">
                  <span className="uw-format__label">Attach</span>
                  <div className="uw-attachments">
                    {MAIL_FORMAT_FILES.map(f => (
                      <span className="uw-attachment" key={f}>
                        <Svg size={14}>
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </Svg>
                        <span className="uw-attachment__name">{f}.pdf</span>
                      </span>
                    ))}
                  </div>
                </div>

                <p className="uw-format__note">
                  Template only: the file names are examples, not real files. Attach your own PDFs, and Underwriting AI
                  works out from each one whether it is a loss run, MVR or CAB report.
                </p>
              </div>
            </div>
          </div>,
          document.body
        )}

        <div className="uw-types">
          {allTypes.map(t => (
            <div className={`uw-type${t.id === 'default' ? ' uw-type--wide' : ''}`} key={t.id}>
              <div className="uw-type__head">
                {/* Decorative — intentionally not a button */}
                <span className="uw-type__icon"><Svg size={16}>{TYPE_ICONS[t.icon]}</Svg></span>
                <span className="uw-type__title">{t.title}</span>
                {t.custom && <span className="uw-badge">Custom</span>}
                {t.custom && (
                  <button
                    className="uw-iconbtn uw-type__delete"
                    title="Delete request type"
                    aria-label={`Delete ${t.title}`}
                    onClick={() => removeType(t)}
                  >
                    <Svg size={16}>
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    </Svg>
                  </button>
                )}
              </div>

              <div className="uw-subject">
                <span className="uw-subject__label">Subject</span>
                <span className="uw-subject__text">
                  {t.id === 'default'
                    ? `${DEFAULT_TYPES.map(d => d.tag).join(' · ')} what you need`
                    : `${t.tag} what you need`}
                </span>
                <button className="uw-iconbtn" title="Copy subject" aria-label={`Copy ${t.title} subject`} onClick={() => copy(`${t.tag} `, 'Subject')}>
                  <Svg size={16}>
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </Svg>
                </button>
                <button className="uw-iconbtn" title="Compose email" aria-label={`Compose ${t.title} email`} onClick={() => compose(`${t.tag} `)}>
                  <Svg size={16}>
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </Svg>
                </button>
              </div>

              <p className="uw-type__desc">{t.description}</p>

              <div className="uw-type__foot">
                <span className="uw-pill uw-pill--muted">Default execution prompt</span>
                <button className="uw-link" onClick={() => notify('info', 'Execution prompt editing is coming soon.')}>
                  <Svg size={15}>
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
                  </Svg>
                  View / edit
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="uw-section uw-section--history">
          <h2 className="uw-section__title">
            <Svg size={17}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </Svg>
            Your inbox history
          </h2>
          <div className="uw-history__tools">
            <div className="search-box search-box--sm">
              <Svg size={15}>
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </Svg>
              <input
                type="text"
                placeholder="Search subject or sender"
                value={historySearch}
                onChange={e => setHistorySearch(e.target.value)}
              />
            </div>
            <button className="uw-iconbtn" title="Refresh" aria-label="Refresh history" onClick={() => setHistoryKey(k => k + 1)}>
              <Svg size={16}>
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </Svg>
            </button>
          </div>
        </div>

        <div className="uw-history">
          <div className="uw-history__list">
            {filteredHistory.length === 0 ? (
              <p className="uw-history__empty">
                {historyError
                  ? historyError
                  : history.length === 0
                    ? `No emails yet. Send one to ${inboxAddress} to see it here.`
                    : 'No conversations match that search.'}
              </p>
            ) : (
              filteredHistory.map(c => (
                <button
                  key={c.id}
                  className={`uw-conv${selectedConv === c.id ? ' uw-conv--active' : ''}`}
                  onClick={() => setSelectedConv(c.id)}
                >
                  <span className="uw-conv__row">
                    <span className="uw-conv__subject">{c.subject}</span>
                    <span className={`uw-status${c.status === 'Failed' ? ' uw-status--failed' : ''}`}>
                      {c.status === 'Replied' && <Svg size={13}><polyline points="20 6 9 17 4 12" /></Svg>}
                      {c.status}
                    </span>
                  </span>
                  <span className="uw-conv__row uw-conv__row--meta">
                    <span className="uw-conv__sender">{c.sender}</span>
                    <span>{c.mails.length} mail · {formatInboxDate(c.date)}</span>
                  </span>
                </button>
              ))
            )}
          </div>

          <div className="uw-history__detail">
            {!activeConv ? (
              <p className="uw-history__hint">
                Select a conversation to view its mail and attachments.
              </p>
            ) : (
              <div className="uw-detail">
                <h3 className="uw-detail__subject">{activeConv.subject}</h3>
                <div className="uw-detail__meta">{activeConv.sender} · {formatInboxDate(activeConv.date)}</div>

                <div className="uw-detail__label">Mails</div>
                {activeConv.mails.map((m, i) => (
                  <div className="uw-mail" key={i}>
                    <div className="uw-mail__from">{m.from}</div>
                    <div className="uw-mail__body">{m.body}</div>
                  </div>
                ))}

                {activeConv.attachments.length > 0 && (
                  <>
                    <div className="uw-detail__label">Attachments</div>
                    <div className="uw-attachments">
                      {activeConv.attachments.map((a, i) => (
                        <span className="uw-attachment" key={i} title={a.filename}>
                          <Svg size={14}>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </Svg>
                          <span className="uw-attachment__name">{a.filename}</span>
                          {a.size > 0 && <span className="uw-attachment__size">{formatFileSize(a.size)}</span>}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnderwritingInbox;
